import type Complaint from '@gov.wa.lni/application.child-labor-complaint/source/components/lni-child-labor-complaint-information/types/Complaint';
import type Contact from '@gov.wa.lni/application.child-labor-complaint/source/components/lni-child-labor-complaint-contact/types/Contact';
import type Employer from '@gov.wa.lni/application.child-labor-complaint/source/components/lni-child-labor-complaint-employer/types/Employer';

type Allegation = {
    TypeCode     : string;
    TypeCodeOther: string;
};

type Attestation = {
    ReferenceType  : string;
    AttestationCode: string;
};

type ClientInformation = {
    BusinessUnit: string;
    ClientCode  : string;
};

type ComplainantInformation = Entity & {
    Category              : string;
    PreferredLanguage     : string;
    OtherPreferredLanguage: string;
    EntityType            : string;
    LicenseNumber         : string;
    FirstName             : string;
    LastName              : string;
    PositionTitle         : string;
    BirthDate             : string;
};

type ComplaintInformation = {
    ComplaintDurationFrom: string;
    ComplaintDurationTo  : string;
    ComplaintDescription : string;
};

type ContactInformation = Entity & {
    ContactPerson: string;
    County       : string; // Instead of CountyCode
    Extension    : string; // Instead of PhoneExtension
};

type Document = {
    ReferenceType: string;
    DocumentType : string;
    DocumentName : string;
    Document     : string;
    Comments     : string;
};

type Entity = {
    AddressTypeCode        : string;
    Street1                : string;
    Street2                : string;
    City                   : string;
    Zip                    : string;
    StateCode              : string;
    CountyCode             : string;
    CountryCode            : string;
    Phone                  : string;
    PhoneExtension         : string;
    AlternatePhone         : string;
    AlternatePhoneExtension: string;
    Fax                    : string;
    Email                  : string;
    AlternateEmail         : string;
};

type PreliminaryQuestion = {
    QuestionnaireType: string;
    QuestionTypeCode : string;
    ResponseType     : string;
    Answer           : string;
};

type Question = {
    QuestionnaireType: string;
    QuestionTypeCode : string;
    ResponseType     : string;
    Answer           : string;
    Explanation      : string;
};

type RespondentInformation = Entity & {
    EntityType            : string;
    LicenseNumber         : string;
    UBINumber             : string;
    FirstName             : string;
    LastName              : string;
    DBAName               : string;
    ContactPerson         : string;
    PreferredLanguage     : string;
    OtherPreferredLanguage: string;
    Address               : Array<ContactInformation>;
};

/**
 * Represents AGRC payload for SaveComplaint submission.
 */
export default class Payload {
    private readonly Allegation            : Array<Allegation>;
    private readonly Attestation           : Array<Attestation>;
    private readonly ClientInformation     : ClientInformation;
    private readonly ComplainantInformation: ComplainantInformation;
    private readonly ComplaintInformation  : ComplaintInformation;
    private readonly Documents             : Array<Document>;
    private readonly PreliminaryQuestion   : Array<PreliminaryQuestion>;
    private readonly Questions             : Array<Question>;
    private readonly RespondentInformation : RespondentInformation;

    /**
     * Converts form data to AGRC payload.
     * @param complaint Data from `lni-child-labor-complaint-information`
     * @param employer Data from `lni-child-labor-complaint-employer`
     * @param documents Data from `lni-child-labor-complaint-documents`
     * @param contact Data from `lni-child-labor-complaint-contact`
     * @param attestation Attestation value from `lni-child-labor-complaint-form`
     */
    constructor(
        complaint: Complaint,
        employer: Employer,
        documents: Array<any>,
        contact: Contact,
        attestation: boolean,
    ) {
        this.ClientInformation = {
            BusinessUnit: 'CL',
            ClientCode  : 'WALNI',
        };

        this.PreliminaryQuestion = [
            {
                QuestionnaireType: '',
                QuestionTypeCode : '',
                ResponseType     : '',
                Answer           : '',
            },
        ];

        const allegations:Array<Allegation> = [];
        complaint.violations.forEach((violation:any) => {
            const allegation:Allegation = {
                TypeCode     : violation.type,
                TypeCodeOther: violation.other?.text,
            };
            allegations.push(allegation);
        });
        this.Allegation = allegations;

        // Add additional information that ALiS does not provide in SaveComplaint to complaint description
        let description = '';
        if (contact.isAnonymous) {
            description = '[ANONYMOUS COMPLAINT]';
        } else {
            if (contact.firstName.length || contact.lastName.length) {
                description = `[CONTACT NAME = ${ contact.firstName } ${ contact.lastName }, RELATIONSHIP = ${ complaint.relationship }]`;
            }
        }
        description = `${ description } ${ complaint.description }`;

        this.ComplaintInformation = {
            ComplaintDurationFrom: complaint.startDate,
            ComplaintDurationTo  : complaint.endDate,
            ComplaintDescription : description,
        };

        // Minor worker information including physical working location
        this.ComplainantInformation = {
            Category               : 'COI',
            PreferredLanguage      : contact.preferredLanguage,
            OtherPreferredLanguage : contact.otherLanguage,
            EntityType             : 'ULE',
            LicenseNumber          : '',
            FirstName              : complaint.firstName,
            LastName               : complaint.lastName,
            AddressTypeCode        : 'MLG',
            Street1                : contact.address1,
            Street2                : contact.address2,
            City                   : contact.city,
            Zip                    : contact.zip,
            StateCode              : contact.state,
            CountryCode            : 'US',
            CountyCode             : '',
            Phone                  : contact.phone,
            PhoneExtension         : contact.ext,
            AlternatePhone         : '',
            AlternatePhoneExtension: '',
            Fax                    : '',
            Email                  : contact.email,
            AlternateEmail         : '',
            PositionTitle          : '',
            BirthDate              : complaint.birthDate,
        };

        this.Questions = [
            getQuestion('CLCMPQS2', 'O', complaint.preliminary),
            getQuestion('CLCMPQS3', 'O', complaint.forMultipleMinors ? 'CLCMPQRES2' : 'CLCMPQRES1'),
            getQuestion('CLCMPQSC4', 'T', complaint.quantity),
            getQuestion('CLCMPQS10', 'D', complaint.workStart),
            getQuestion('CLCMPQS11', 'D', complaint.workEnd),
            getQuestion('CLCMPQS12', 'O', complaint.isTerminated ? 'CLCMPQRESN2' : 'CLCMPQRESY1'),
            getQuestion('CLCMPQS14', 'L', complaint.endReason, complaint.otherReason),
        ];

        const employerContact:ContactInformation = {
            ContactPerson          : '',
            AddressTypeCode        : 'PHL',
            Street1                : employer.locationAddress1,
            Street2                : employer.locationAddress2,
            City                   : employer.locationCity,
            Zip                    : employer.locationZip,
            StateCode              : employer.locationState,
            CountryCode            : 'US',
            CountyCode             : '', // Ignore use County
            County                 : '',
            Phone                  : '',
            PhoneExtension         : '', // Ignore use Extension
            Extension              : '',
            AlternatePhone         : '',
            AlternatePhoneExtension: '',
            Fax                    : '',
            Email                  : '',
            AlternateEmail         : '',
        };

        this.RespondentInformation = {
            EntityType             : 'ULE',
            LicenseNumber          : '',
            UBINumber              : '000 000 000', // UBI is required but not used
            FirstName              : '',
            LastName               : '',
            AddressTypeCode        : 'MLG',
            Street1                : employer.address1,
            Street2                : employer.address2,
            City                   : employer.city,
            Zip                    : employer.zip,
            StateCode              : employer.state,
            CountryCode            : 'US',
            CountyCode             : '',
            Phone                  : employer.phone,
            PhoneExtension         : employer.ext,
            AlternatePhone         : employer.contactPhone,
            AlternatePhoneExtension: '',
            Fax                    : '',
            Email                  : employer.email,
            AlternateEmail         : '',
            DBAName                : employer.name,
            ContactPerson          : `${ employer.contactFirstName } ${ employer.contactLastName }`,
            PreferredLanguage      : '',
            OtherPreferredLanguage : '',
            Address                : [employerContact],
        };

        this.Documents = [];
        for (const document of documents) {
            this.Documents.push({
                ReferenceType: 'CMP',
                DocumentType : document.DocumentType,
                DocumentName : document.FileName,
                Document     : document.FileData,
                Comments     : document.FileDescription,
            });
        }

        this.Attestation = [];
        if (attestation) {
            this.Attestation.push({
                ReferenceType  : 'CMP',
                AttestationCode: 'CLATT1',
            });
        }
    }

    get data() {
        return {
            ClientInformation     : this.ClientInformation,
            ComplainantInformation: this.ComplainantInformation,
            ComplaintInformation  : this.ComplaintInformation,
            RespondentInformation : this.RespondentInformation,
            Allegation            : this.Allegation,
            PreliminaryQuestion   : this.PreliminaryQuestion,
            Questions             : this.Questions,
            Documents             : this.Documents,
            Attestation           : this.Attestation,
        };
    }
}

/**
 * Helper to create Question.
 * @param questionTypeCode The `QuestionTypeCode` provided by AGRC
 * @param responseType The `ResponseType` provided by AGRC
 * @param answer The value from the form data
 * @param explanation The additional value from the form data (if needed)
 */
const getQuestion = (questionTypeCode: string, responseType: string, answer: string, explanation = '') => ({
    QuestionnaireType: 'CLCAI',
    QuestionTypeCode : questionTypeCode,
    ResponseType     : responseType,
    Answer           : answer,
    Explanation      : explanation,
});